import {
  Alert,
  Box,
  Button,
  Dialog,
  HStack,
  Input,
  NativeSelect,
  Portal,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useState } from "react";
import _ from "lodash";
import { useMerchant } from "@/hooks/useMerchant";
import { useCreateTopUp, type TopUpBank } from "../hooks/useCreateTopUp";
import { useTRPC } from "@/trpc/client";
import { useQuery } from "@tanstack/react-query";
import { toaster } from "../../../../components/ui/toaster";

const MIN_AMOUNT = 1500;

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(value);

interface TopUpModalProps {
  isOpen: boolean;
  onClose: () => void;
  batchId?: string;
  shortfall?: number;
}

export default function TopUpModal({
  isOpen,
  onClose,
  batchId,
  shortfall,
}: TopUpModalProps) {
  return (
    <Dialog.Root
      lazyMount
      unmountOnExit
      open={isOpen}
      onOpenChange={(e) => !e.open && onClose()}
      size="md"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <TopUpForm
              onClose={onClose}
              batchId={batchId}
              shortfall={shortfall}
            />
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}

function TopUpForm({
  onClose,
  batchId,
  shortfall,
}: Omit<TopUpModalProps, "isOpen">) {
  const { merchant } = useMerchant();
  const fee = merchant?.paymentMethods.pse.fee ?? 0;
  const minAmount = Math.max(MIN_AMOUNT, fee + 1, (shortfall ?? 0) + fee);

  const [amount, setAmount] = useState(() =>
    shortfall ? String(minAmount) : ""
  );
  const [bank, setBank] = useState<TopUpBank | "">("");
  const [isRedirecting, setRedirecting] = useState(false);
  const { createTopUp, isCreating } = useCreateTopUp();
  const trpc = useTRPC();
  const { data: catalogs } = useQuery(trpc.onboarding.catalogs.queryOptions());

  const parsedAmount = parseInt(amount, 10) || 0;
  const net = parsedAmount - fee;
  const isValidAmount = parsedAmount >= minAmount;
  const minAmountError = amount && parsedAmount > 0 && !isValidAmount;
  const canSubmit = isValidAmount && bank !== "";

  const handleSubmit = async () => {
    if (!isValidAmount || bank === "") return;
    try {
      const topUp = await createTopUp(parsedAmount, bank, batchId);
      setRedirecting(true);
      window.location.assign(topUp.paymentUrl);
    } catch {
      toaster.create({
        title: "Error",
        description: "No se pudo generar el pago PSE. Intenta de nuevo.",
        type: "error",
        duration: 5000,
        meta: { closable: true },
      });
    }
  };

  return (
    <>
      <Dialog.Header borderBottom="1px solid #ebecef80" p={4}>
        <Dialog.Title fontSize="lg" fontWeight="semibold">
          Cargar fondos por PSE
        </Dialog.Title>
      </Dialog.Header>
      <Dialog.CloseTrigger position="top-end" top={3} />
      <Dialog.Body py={6}>
        <VStack gap={4} align="stretch">
          {shortfall ? (
            <Alert.Root status="warning" colorPalette="orange">
              <Alert.Indicator />
              <Alert.Description fontSize="sm">
                Faltan {formatCurrency(shortfall)} para dispersar este lote. Si
                cargas más, el excedente queda como saldo disponible.
              </Alert.Description>
            </Alert.Root>
          ) : null}
          <Box>
            <Text fontWeight="medium" mb={2}>
              Monto a cargar
            </Text>
            <Input
              type="text"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="0"
              size="lg"
            />
            {amount && (
              <Text fontSize="sm" color="gray.500" mt={1} fontWeight="medium">
                {formatCurrency(parsedAmount)}
              </Text>
            )}
            {minAmountError && (
              <Text fontSize="sm" color="red.500" mt={1}>
                {shortfall
                  ? `El monto mínimo para dispersar este lote es ${formatCurrency(
                      minAmount
                    )}`
                  : `El monto mínimo es de ${formatCurrency(minAmount)}`}
              </Text>
            )}
          </Box>
          <Box>
            <Text fontWeight="medium" mb={2}>
              Banco
            </Text>
            <NativeSelect.Root size="lg">
              <NativeSelect.Field
                value={bank}
                onChange={(e) => setBank(e.target.value as TopUpBank)}
                placeholder="Selecciona el banco"
              >
                {_.sortBy(catalogs?.pseBanks, "label").map((b) => (
                  <option key={b.value} value={b.value}>
                    {b.label}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
            <Text fontSize="sm" color="gray.500" mt={1}>
              Los fondos se cargan por PSE desde la cuenta de tu empresa en este
              banco.
            </Text>
          </Box>
          <Box
            p={4}
            bg="gray.50"
            borderRadius="lg"
            border="1px solid"
            borderColor="gray.200"
          >
            <HStack justify="space-between">
              <Text fontSize="sm" color="gray.500">
                Costo PSE
              </Text>
              <Text fontSize="sm" fontWeight="medium">
                {formatCurrency(fee)}
              </Text>
            </HStack>
            <HStack justify="space-between" mt={2}>
              <Text fontSize="sm" color="gray.500">
                Saldo a acreditar
              </Text>
              <Text fontSize="sm" fontWeight="bold">
                {formatCurrency(Math.max(net, 0))}
              </Text>
            </HStack>
          </Box>
          <Text fontSize="xs" color="gray.500">
            Al continuar te llevamos a tu banco para completar el pago.
          </Text>
        </VStack>
      </Dialog.Body>
      <Dialog.Footer borderTop="1px solid #ebecef80" p={4}>
        <Button
          variant="ghost"
          onClick={onClose}
          disabled={isCreating || isRedirecting}
        >
          Cancelar
        </Button>
        <Button
          colorPalette="palomma"
          onClick={handleSubmit}
          loading={isCreating || isRedirecting}
          disabled={!canSubmit}
        >
          Cargar fondos
        </Button>
      </Dialog.Footer>
    </>
  );
}
